import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import useNotify from './useNotify';
import { gql } from '@/modes/common/gql';

export type TempFile = {
  key: string;
  contentType: string;
  url: string;
  size: number;
};

const CREATE_UPLOAD = gql(`
  mutation CreateUpload($contentType: String!, $size: Int!) {
    createUpload(contentType: $contentType, size: $size) {
      key
      contentType
      url
      uploadUrl
      expires
    }
  }
`);

export const useUploadTempFile = () => {
  const { showError } = useNotify();
  const [uploading, setUploading] = useState(false);

  const [createUpload, { loading: createUploadLoading }] = useMutation<
    {
      createUpload: {
        key: string;
        contentType: string;
        url: string;
        uploadUrl: string;
        expires: number;
      };
    },
    {
      contentType: string;
      size: number;
    }
  >(CREATE_UPLOAD, {
    onError: (error) => {
      console.error('Error starting upload', error);
      showError('Error starting upload', error);
    },
  });

  const uploadTempFile = useCallback(
    async (selectedFile: File): Promise<TempFile> => {
      const { data: uploadData } = await createUpload({
        variables: {
          contentType: selectedFile.type,
          size: selectedFile.size,
        },
      });
      if (!uploadData) {
        throw new Error('createUpload returned empty result');
      }
      const {
        createUpload: { uploadUrl, key, contentType, url, expires },
      } = uploadData;

      setUploading(true);

      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
            'Content-Length': selectedFile.size + '',
          },
          mode: 'cors',
          credentials: 'omit',
          body: selectedFile,
        });
        if (!response.ok) {
          throw new Error('File upload request failed');
        }
      } finally {
        setUploading(false);
      }

      return {
        key,
        contentType,
        url,
        size: selectedFile.size,
      };
    },
    [createUpload]
  );

  const uploadTempFiles = useCallback(
    (acceptedFiles: File[] | readonly File[]): Promise<TempFile[]> => {
      return Promise.all(acceptedFiles.map(uploadTempFile));
    },
    [uploadTempFile]
  );

  return {
    loading: createUploadLoading || uploading,
    uploadTempFile,
    uploadTempFiles,
  };
};
